import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import {
  FaAngleDown,
  FaBars,
  FaTimes,
  FaSun,
  FaMoon,
  FaSeedling,
  FaPlus,
  FaChartLine,
  FaUsers,
  FaSignOutAlt,
  FaShieldAlt,
  FaBell,
  FaRegBell,
  FaQrcode,
  FaBookOpen,
  FaInfoCircle,
} from "react-icons/fa";
import { motion, AnimatePresence } from "framer-motion";
import { 
  getFields,
  getUnreadNotificationsCount, 
} from "../services/apiService";
import { getResetRequestsCount } from "../services/adminApiService";
import { useTheme } from "../contexts/ThemeContext";
import { useScanner } from "../contexts/ScannerContext";
import { useSocket } from "../contexts/SocketContext";
import { invalidateCache, setCache } from "../utils/apiCache";
import favicon from "../assets/favicon.webp";
import UserAvatar from './UserAvatar';
import GoogleTranslate from './GoogleTranslate';
import '../styles/Header.css';
import '../styles/GoogleTranslate.css';

const Header = () => {
  const navigate = useNavigate();
  const { toggleTheme, isDark } = useTheme();
  const { openScanner } = useScanner();
  const { useSocketEvent } = useSocket();
  const [userInfo, setUserInfo] = useState(null);
  const [userName, setUserName] = useState("");
  const [adminDropdownOpen, setAdminDropdownOpen] = useState(false);
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [farmerFieldCount, setFarmerFieldCount] = useState(0);
  const [isFetchingFields, setIsFetchingFields] = useState(true);
  const [pendingResetCount, setPendingResetCount] = useState(0);
  const [unreadNotifCount, setUnreadNotifCount] = useState(0);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const adminDropdownRef = useRef(null);
  const userDropdownRef = useRef(null);

  useEffect(() => {
    const handleAuthChange = () => {
      const newStoredUserInfo = JSON.parse(localStorage.getItem("userInfo"));
      setUserInfo(newStoredUserInfo);
      setUserName(newStoredUserInfo?.name || "");
    };

    handleAuthChange();
    window.addEventListener("authchange", handleAuthChange);

    return () => window.removeEventListener("authchange", handleAuthChange);
  }, []);

  const fetchUnreadCount = useCallback(async () => {
    if (userInfo && userInfo.role === "FARMER") {
      try {
        const count = await getUnreadNotificationsCount();
        setUnreadNotifCount(count);
      } catch (error) {
        console.error("Error fetching notifications:", error);
      }
    }
  }, [userInfo]);

  useEffect(() => {
    let interval;
    if (userInfo && userInfo.role === "FARMER") {
      fetchUnreadCount();
      interval = setInterval(fetchUnreadCount, 60000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [userInfo, fetchUnreadCount]);

  // Real-time Notification Listeners via SocketContext
  useSocketEvent('notification:new', (data) => {
    if (userInfo?.role === "FARMER") {
        if (data && typeof data.unreadCount === 'number') {
            setUnreadNotifCount(data.unreadCount);
            setCache('notifications_unread_count', data.unreadCount);
        } else {
            invalidateCache('notifications_unread_count');
            fetchUnreadCount();
        }
    }
  });

  useSocketEvent('notification:update_count', (data) => {
    if (userInfo?.role === "FARMER") {
        if (data && typeof data.unreadCount === 'number') {
            setUnreadNotifCount(data.unreadCount);
            setCache('notifications_unread_count', data.unreadCount);
        } else {
            invalidateCache('notifications_unread_count');
            fetchUnreadCount();
        }
    }
  });

  useEffect(() => {
    let interval;
    const fetchCount = async () => {
      if (userInfo && userInfo.role === "ADMIN") {
        try {
          const { count } = await getResetRequestsCount();
          setPendingResetCount(count);
        } catch (error) {
          console.error("Error fetching reset count:", error);
        }
      }
    };

    if (userInfo && userInfo.role === "ADMIN") {
      fetchCount();
      interval = setInterval(fetchCount, 30000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [userInfo]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (adminDropdownRef.current && !adminDropdownRef.current.contains(event.target)) {
        setAdminDropdownOpen(false);
      }
      if (userDropdownRef.current && !userDropdownRef.current.contains(event.target)) {
        setUserDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const fetchFarmerFields = async () => {
      if (userInfo && userInfo.role === "FARMER") {
        setIsFetchingFields(true);
        try {
          const fields = await getFields();
          setFarmerFieldCount(fields.length);
        } catch (error) {
          console.error("Error fetching farmer fields:", error);
          setFarmerFieldCount(0);
        } finally {
          setIsFetchingFields(false);
        }
      } else {
        setFarmerFieldCount(0);
        setIsFetchingFields(false);
      }
    };
    fetchFarmerFields();
  }, [userInfo]);

  const logoutHandler = async () => {
    if ("caches" in window) {
      try {
        const cacheNames = await caches.keys();
        await Promise.all(cacheNames.map((name) => {
            if (name.includes("api-farmer-data")) return caches.delete(name);
            return Promise.resolve();
        }));
      } catch (err) {
        console.error("Error clearing caches:", err);
      }
    }
    localStorage.removeItem("userInfo");
    window.dispatchEvent(new Event('authchange'));
    setUserInfo(null);
    setUserName("");
    navigate("/login");
  };

  const getHomeLink = () => {
    if (!userInfo) return "/login";
    if (userInfo.role === "ADMIN") return "/admin/dashboard";
    if (userInfo.role === "FARMER") {
      if (isFetchingFields) return "/journey/welcome";
      return farmerFieldCount > 0 ? "/dashboard" : "/journey/welcome";
    }
    return "/login";
  };

  const toggleAdminDropdown = () => setAdminDropdownOpen(!adminDropdownOpen);
  const toggleUserDropdown = () => setUserDropdownOpen(!userDropdownOpen);
  const closeDropdowns = () => {
    setAdminDropdownOpen(false);
    setUserDropdownOpen(false);
  };

  const navLinkClassName = ({ isActive }) => isActive ? "nav-link active" : "nav-link";

  const closeMobileMenu = () => setMobileMenuOpen(false);

  return (
    <header className={`header select-none ${scrolled ? "header-scrolled" : ""}`}>
      <div className="header-container-responsive">
        <div className="header-nav">
          <Link to={getHomeLink()} className="logo-link">
            <img src={favicon} alt="Logo" />
            <span>Naturextrakte OBFS</span>
          </Link>

          <div className="mobile-language-wrap">
            <GoogleTranslate id="google_translate_element_mobile_header" />
          </div>

          <nav className="nav-desktop">
            <ul>
              {userInfo ? (
                <>
                  {userInfo.role === "FARMER" && (
                    <>
                      <li><NavLink to="/dashboard" className={navLinkClassName}>Dashboard</NavLink></li>
                      <li><NavLink to="/fields" className={navLinkClassName}>My Fields</NavLink></li>
                      <li>
                        <button onClick={openScanner} className="nav-link flex items-center gap-2" title="Scan QR">
                          <FaQrcode /> <span>Scan</span>
                        </button>
                      </li>
                    </>
                  )}
                  <li><NavLink to="/feed" className={navLinkClassName}>Community</NavLink></li>
                  {userInfo.role === "FARMER" && (
                    <li className="relative">
                      <NavLink to="/notifications" className={navLinkClassName}>
                        {unreadNotifCount > 0 ? <FaBell className="text-warning" /> : <FaRegBell />}
                        {unreadNotifCount > 0 && <span className="notification-dot">{unreadNotifCount}</span>}
                      </NavLink>
                    </li>
                  )}
                  {userInfo.role === "ADMIN" && (
                    <li className="nav-dropdown" ref={adminDropdownRef}>
                      <button onClick={toggleAdminDropdown} className="nav-link dropdown-toggle">
                        <span>Admin</span>
                        {pendingResetCount > 0 && <span className="badge-danger">{pendingResetCount}</span>}
                        <FaAngleDown />
                      </button>
                      {adminDropdownOpen && (
                        <div className="nav-dropdown-menu">
                          <NavLink to="/admin/farmers" onClick={closeDropdowns} className="nav-dropdown-item">Farmers</NavLink>
                          <NavLink to="/admin/requests" onClick={closeDropdowns} className="nav-dropdown-item">
                            Resets {pendingResetCount > 0 && <span className="badge-danger">{pendingResetCount}</span>}
                          </NavLink>
                          <NavLink to="/admin/master/crops" onClick={closeDropdowns} className="nav-dropdown-item">Crop Master</NavLink>
                          <NavLink to="/admin/master/growth-stages" onClick={closeDropdowns} className="nav-dropdown-item">Growth Stages</NavLink>
                          <NavLink to="/admin/master/fertilizers" onClick={closeDropdowns} className="nav-dropdown-item">Fertilizers</NavLink>
                          <NavLink to="/admin/master/pesticides" onClick={closeDropdowns} className="nav-dropdown-item">Pesticides</NavLink>
                          <NavLink to="/admin/master/insecticides" onClick={closeDropdowns} className="nav-dropdown-item">Insecticides</NavLink>
                          <NavLink to="/admin/master/standard-dosages" onClick={closeDropdowns} className="nav-dropdown-item">Dosages</NavLink>
                          <NavLink to="/admin/master/bulk-upload" onClick={closeDropdowns} className="nav-dropdown-item">Bulk Upload</NavLink>
                          <NavLink to="/admin/agronomic-master" onClick={closeDropdowns} className="nav-dropdown-item">Agronomic</NavLink>
                          <NavLink to="/admin/blogs" onClick={closeDropdowns} className="nav-dropdown-item">Manage Blogs</NavLink>
                          <div className="dropdown-divider"></div>
                          <NavLink to="/admin/recommendations" onClick={closeDropdowns} className="nav-dropdown-item">Recommendations</NavLink>
                          <NavLink to="/admin/notifications" onClick={closeDropdowns} className="nav-dropdown-item">Push Notif</NavLink>
                        </div>
                      )}
                    </li>
                  )}
                  <li>
                    <button onClick={toggleTheme} className="theme-toggle">
                      {isDark ? <FaSun /> : <FaMoon />}
                    </button>
                  </li>
                  <li>
                    <GoogleTranslate id="google_translate_element_header" />
                  </li>
                  <li className="nav-dropdown" ref={userDropdownRef}>
                    <button onClick={toggleUserDropdown} className="nav-link dropdown-toggle">
                      <UserAvatar user={userInfo} size={24} />
                      <span>{userName}</span>
                      <FaAngleDown />
                    </button>
                    {userDropdownOpen && (
                      <div className="nav-dropdown-menu">
                        <NavLink to="/profile" onClick={closeDropdowns} className="nav-dropdown-item">Profile</NavLink>
                        <button onClick={() => { logoutHandler(); closeDropdowns(); }} className="nav-dropdown-item">Logout</button>
                      </div>
                    )}
                  </li>
                </>
              ) : (
                <>
                  <li><NavLink to="/login" className="nav-link">Sign In</NavLink></li>
                  <li><NavLink to="/register" className="btn-primary">Sign Up</NavLink></li>
                  <li><GoogleTranslate id="google_translate_element_guest" /></li>
                  <li><button onClick={toggleTheme} className="theme-toggle">{isDark ? <FaSun /> : <FaMoon />}</button></li>
                </>
              )}
            </ul>
          </nav>

          <div className="mobile-nav-controls">
            {userInfo && userInfo.role === "FARMER" && (
              <NavLink to="/notifications" className="relative p-2">
                {unreadNotifCount > 0 ? <FaBell className="text-warning w-5 h-5" /> : <FaRegBell className="w-5 h-5" />}
              </NavLink>
            )}
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="mobile-menu-button">
              {mobileMenuOpen ? <FaTimes /> : <FaBars />}
            </button>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {mobileMenuOpen && (
          <>
            <motion.div className="mobile-overlay" onClick={closeMobileMenu} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} />
            <motion.div className="mobile-menu-drawer" initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }} transition={{ type: "spring", damping: 25, stiffness: 200 }}>
              <div className="mobile-menu-header">
                <span className="mobile-menu-title">Menu</span>
                <button onClick={closeMobileMenu} className="mobile-close-btn"><FaTimes /></button>
              </div>
              <div className="mobile-menu-content">
                {userInfo ? (
                  <>
                    <div className="mobile-user-profile">
                      <UserAvatar user={userInfo} size={48} />
                      <div className="mobile-user-info">
                        <p className="mobile-user-name">{userName}</p>
                        <p className="mobile-user-role">{userInfo.role}</p>
                      </div>
                    </div>
                    <div className="mobile-nav-links">
                      {userInfo.role === "FARMER" && (
                        <div className="mobile-nav-group">
                          <span className="mobile-group-title">Field Management</span>
                          <NavLink to="/dashboard" onClick={closeMobileMenu} className="mobile-nav-item"><FaChartLine /> Dashboard</NavLink>
                          <NavLink to="/fields" onClick={closeMobileMenu} className="mobile-nav-item"><FaSeedling /> My Fields</NavLink>
                          <div onClick={() => { openScanner(); closeMobileMenu(); }} className="mobile-nav-item"><FaQrcode /> Scan QR</div>
                          <NavLink to="/notifications" onClick={closeMobileMenu} className="mobile-nav-item flex justify-between">
                            <span className="flex items-center gap-3"><FaBell /> Notifications</span>
                            {unreadNotifCount > 0 && <span className="notification-badge-mobile">{unreadNotifCount}</span>}
                          </NavLink>
                          <NavLink to="/journey/welcome" onClick={closeMobileMenu} className="mobile-nav-item"><FaPlus /> New Field</NavLink>
                        </div>
                      )}
                      <NavLink to="/feed" onClick={closeMobileMenu} className="mobile-nav-item"><FaUsers /> Community</NavLink>
                      <NavLink to="/blogs" onClick={closeMobileMenu} className="mobile-nav-item"><FaBookOpen /> Blog</NavLink>
                      <NavLink to="/about" onClick={closeMobileMenu} className="mobile-nav-item"><FaInfoCircle /> About Us</NavLink>
                      {userInfo.role === "ADMIN" && (
                        <div className="mobile-nav-group">
                          <span className="mobile-group-title">Admin Operations</span>
                          <NavLink to="/admin/dashboard" onClick={closeMobileMenu} className="mobile-nav-item"><FaChartLine /> Admin Dashboard</NavLink>
                          <NavLink to="/admin/farmers" onClick={closeMobileMenu} className="mobile-nav-item"><FaUsers /> Farmers</NavLink>
                          <NavLink to="/admin/requests" onClick={closeMobileMenu} className="mobile-nav-item"><FaShieldAlt /> Resets</NavLink>
                        </div>
                      )}
                    </div>
                    
                    <div className="mobile-drawer-language-selector">
                      <span className="mobile-group-title">Language / भाषा</span>
                      <div className="mt-2">
                        <GoogleTranslate id="google_translate_element_mobile" />
                      </div>
                    </div>
                    <div className="mobile-nav-footer">
                      <NavLink to="/profile" onClick={closeMobileMenu} className="mobile-nav-item"><UserAvatar user={userInfo} size={20} /> Profile</NavLink>
                      <button onClick={toggleTheme} className="mobile-nav-item">{isDark ? <FaSun /> : <FaMoon />} {isDark ? "Light Mode" : "Dark Mode"}</button>
                      <button onClick={() => { logoutHandler(); closeMobileMenu(); }} className="mobile-nav-item text-danger"><FaSignOutAlt /> Logout</button>
                    </div>
                  </>
                ) : (
                  <div className="flex flex-col gap-6">
                    <div className="mobile-auth-buttons">
                      <NavLink to="/login" onClick={closeMobileMenu} className="mobile-nav-item">Sign In</NavLink>
                      <NavLink to="/register" onClick={closeMobileMenu} className="btn-primary w-full text-center py-3">Sign Up</NavLink>
                    </div>
                    <div className="mobile-nav-group pt-4 border-t border-border-main/20">
                      <span className="mobile-group-title">Language</span>
                      <div className="px-4 py-2">
                        <GoogleTranslate id="google_translate_element_mobile_guest" />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
